import { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  CalendarCheck,
  Heart,
  MapPin,
  MessageCircle,
  Plane,
  ReceiptText,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useHostEntryPath } from '@/apis/chat/hostEntry';
import MotionSparkles from '@/components/atoms/MotionSparkles';
import HomeFaqQuestions from '@/components/organisms/HomeFaqQuestions';

const HomeHostCarousel = lazy(
  () => import('@/components/organisms/HomeHostCarousel'),
);

const journeySteps = [
  { key: 'consult', icon: MessageCircle },
  { key: 'proposal', icon: Sparkles },
  { key: 'payment', icon: ReceiptText },
  { key: 'booking', icon: CalendarCheck },
] as const;
const quickLinks = [
  { key: 'trips', to: '/explore', icon: Plane },
  { key: 'treatments', to: '/explore?tab=treatments', icon: Sparkles },
  { key: 'stories', to: '/journeys', icon: Heart },
  { key: 'guide', to: '/trends', icon: MapPin },
] as const;
const HomeContentCollections = lazy(
  () => import('@/components/organisms/HomeContentCollections'),
);

/** C-01 uses current public APIs; unpublished and empty sections keep an explicit state. */
export default function MvpHome() {
  const { t } = useTranslation(),
    hostEntry = useHostEntryPath();
  return (
    <div className="pb-24">
      <section
        className="relative isolate min-h-[340px] overflow-hidden bg-gp-cream px-5 pt-5 max-[359px]:min-h-[276px] max-[359px]:pt-4"
        aria-labelledby="glow-home-title"
      >
        <img
          data-entry-image
          src="/images/seoul-glow-hero.webp"
          srcSet="/images/seoul-glow-hero-768.webp 768w, /images/seoul-glow-hero.webp 1024w"
          sizes="(min-width: 480px) 480px, 100vw"
          width={1024}
          height={1280}
          alt=""
          fetchPriority="high"
          className="gp-hero-image absolute inset-0 -z-20 size-full object-cover object-[center_42%]"
        />
        <div className="absolute inset-0 -z-10 bg-linear-to-b from-gp-cream/65 via-gp-pink-50/0 to-gp-pink-50/75" />
        <MotionSparkles />
        <p className="gp-hero-eyebrow mb-3 text-[10px] font-semibold tracking-[0.16em] text-gp-pink-800">
          {t('mvp.home.eyebrow')}
        </p>
        <h1
          id="glow-home-title"
          className="gp-hero-title max-w-[260px] font-display text-display font-semibold tracking-tight text-gp-pink-800 max-[359px]:max-w-[240px] max-[359px]:text-[26px] max-[359px]:leading-[30px]"
        >
          {t('mvp.home.title')}
        </h1>
        <p className="gp-hero-subtitle mt-3 max-w-[220px] rounded-xl bg-gp-cream/95 px-3 py-2 text-[13px] leading-5 text-gp-ink-900 max-[359px]:max-w-[190px]">
          {t('mvp.home.body')}
          <span
            className="mt-1 block text-[10px] leading-4 text-gp-pink-800 max-[359px]:hidden"
            aria-hidden="true"
          >
            {t('brand.script')}
          </span>
        </p>
      </section>

      <HomeFaqQuestions className="mx-4 -mt-6" />

      <Suspense
        fallback={
          <div
            className="mx-4 mt-3 h-[228px] rounded-card bg-white/95"
            aria-busy="true"
          />
        }
      >
        <HomeHostCarousel />
      </Suspense>

      <nav
        data-gp-stagger
        className="mx-4 mt-4 grid grid-cols-4 gap-2"
        aria-label={t('home:quickMenu.label')}
      >
        {quickLinks.map(({ key, to, icon: Icon }) => (
          <Link
            key={key}
            to={to}
            className="gp-card flex min-h-24 min-w-0 flex-col items-center justify-center gap-2 rounded-input border border-gp-pink-100/60 bg-white/80 px-1 py-3 text-center"
          >
            <Icon
              data-gp-reveal="icon"
              className="size-6 text-gp-pink-700"
              aria-hidden="true"
            />
            <span className="text-[11px] font-semibold leading-4">
              {t(`home:quickMenu.${key}`)}
            </span>
          </Link>
        ))}
      </nav>

      <section
        data-gp-reveal
        className="mx-4 mt-6 rounded-card bg-gp-cream p-4"
        aria-labelledby="glow-home-worries"
      >
        <h2
          id="glow-home-worries"
          className="font-display text-h2 font-semibold"
        >
          {t('home:worries.title')}
        </h2>
        <p className="mt-2 text-caption leading-5 text-gp-ink-600">
          {t('home:actual.worries')}
        </p>
        <Link
          to="/diagnosis/1"
          className="mt-2 inline-flex min-h-11 items-center gap-2 text-caption font-semibold text-gp-pink-700"
        >
          {t('mvp.home.diagnosis')}
          <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </section>

      <Link
        to="/explore"
        data-gp-reveal
        className="gp-card relative isolate mx-4 mt-6 flex min-h-36 items-center overflow-hidden rounded-card border border-gp-pink-100 p-5"
      >
        <img
          data-gp-media
          src="/images/seoul-glow-hero.webp"
          srcSet="/images/seoul-glow-hero-768.webp 768w, /images/seoul-glow-hero.webp 1024w"
          sizes="(min-width: 480px) 480px, 100vw"
          width={1024}
          height={1280}
          alt=""
          loading="lazy"
          className="absolute inset-0 -z-20 size-full object-cover object-[center_42%]"
        />
        <span className="absolute inset-0 -z-10 bg-linear-to-r from-gp-cream/95 via-gp-cream/80 to-gp-cream/15" />
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-semibold tracking-[0.12em] text-gp-pink-800">
            {t('brand.tagline')}
          </span>
          <span className="mt-2 block font-display text-display font-semibold text-gp-pink-800">
            {t('home:actual.seoul')}
          </span>
          <span className="mt-1 block text-caption text-gp-ink-900">
            {t('home:actual.explore')}
          </span>
        </span>
        <ArrowRight
          className="ml-3 size-5 shrink-0 text-gp-pink-700"
          aria-hidden="true"
        />
      </Link>

      <Link
        to="/passes"
        data-gp-reveal
        className="gp-card mx-4 mt-5 flex min-h-24 items-center gap-3 rounded-card border border-gp-pink-100 bg-white p-5 shadow-glow"
      >
        <ReceiptText
          data-gp-reveal="icon"
          aria-hidden
          className="size-6 shrink-0 text-gp-pink-700"
        />
        <span className="min-w-0 flex-1">
          <span className="block font-display text-h3">
            {t('home:actual.passesTitle')}
          </span>
          <span className="mt-1 block text-caption text-gp-ink-600">
            {t('home:actual.passesHelp')}
          </span>
        </span>
        <ArrowRight aria-hidden className="size-5 shrink-0 text-gp-pink-700" />
      </Link>

      <Suspense
        fallback={
          <div
            className="mx-4 mt-8 h-64 rounded-card bg-white/80"
            aria-busy="true"
          />
        }
      >
        <HomeContentCollections />
      </Suspense>

      <section
        data-gp-reveal
        className="mx-4 mt-8 rounded-card border border-gp-pink-100 bg-gp-pink-100/60 p-5"
        aria-labelledby="glow-home-final"
      >
        <h2 id="glow-home-final" className="font-display text-h1 font-semibold">
          {t('home:finalCta.title')}
        </h2>
        <p className="mt-3 text-body leading-relaxed text-gp-ink-600">
          {t('home:finalCta.body')}
        </p>
        <Button asChild block size="lg" className="gp-home-cta mt-4">
          <Link to={hostEntry}>
            {t('home:finalCta.button')}
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
        <Link
          to="/chat"
          className="mt-2 flex min-h-11 items-center justify-center gap-2 text-center text-caption font-semibold text-gp-pink-800"
        >
          {t('brand.talkToHost')}
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
        <p className="text-center text-[10px] text-gp-ink-600">
          {t('mvp.home.hostNote')}
        </p>
      </section>

      <section
        data-gp-reveal
        className="px-4 pt-8"
        aria-labelledby="glow-journey-steps"
      >
        <p className="text-[10px] font-semibold tracking-[0.16em] text-gp-pink-800">
          {t('mvp.home.stepsEyebrow')}
        </p>
        <h2
          id="glow-journey-steps"
          className="mt-1 font-display text-h2 font-semibold"
        >
          {t('mvp.home.stepsTitle')}
        </h2>
        <ol data-gp-stagger className="mt-3 grid grid-cols-2 gap-3">
          {journeySteps.map(({ key, icon: Icon }, index) => (
            <li
              key={key}
              className="rounded-card border border-gp-pink-100/70 bg-white/75 p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <Icon
                  data-gp-reveal="icon"
                  className="size-4 text-gp-pink-800"
                  aria-hidden="true"
                />
                <span
                  className="font-display text-caption text-gp-ink-600"
                  aria-hidden="true"
                >
                  0{index + 1}
                </span>
              </div>
              <h3 className="text-caption font-semibold">
                {t(`mvp.home.stepTitles.${key}`)}
              </h3>
              <p className="mt-1 text-[11px] leading-4 text-gp-ink-600">
                {t(`mvp.home.steps.${key}`)}
              </p>
            </li>
          ))}
        </ol>
      </section>
      <footer className="mt-8 px-4 text-center text-caption text-gp-ink-600">
        <nav
          className="flex flex-wrap justify-center gap-x-4"
          aria-label={t('home:actual.footer')}
        >
          <Link
            className="inline-flex min-h-11 items-center text-gp-pink-800 underline underline-offset-4"
            to="/help"
          >
            {t('screens:help')}
          </Link>
          {(['privacy', 'terms', 'patient-rights'] as const).map((type) => (
            <Link
              key={type}
              className="inline-flex min-h-11 items-center text-gp-pink-800 underline underline-offset-4"
              to={`/legal/${type}`}
            >
              {t(
                `footer.${type === 'patient-rights' ? 'patientRights' : type}`,
              )}
            </Link>
          ))}
        </nav>
        <p className="mt-1">{t('footer.copyright')}</p>
      </footer>
    </div>
  );
}
